"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "@/hooks/useTranslation";
import { Treatment } from "@/lib/treatments-data";
import { ensureStringArray } from "@/lib/utils";
import Header from "@/components/layout/header";
import Breadcrumbs from "@/components/Breadcrumbs";
import AllOnFourHeroSection from "./all-on-4/hero-section";
import WhatIsAllOnFourSection from "./all-on-4/what-is-section";
import WhyChooseSection from "./all-on-4/why-choose-section";
import TreatmentTimelineSection from "./all-on-4/treatment-timeline-section";
import TypesOfImplantsSection from "./all-on-4/types-section";
import BenefitsSection from "./all-on-4/benefits-section";
import GallerySection from "./all-on-4/gallery-section";
import CostSection from "./all-on-4/cost-section";
import CandidateSection from "./all-on-4/candidate-section";
import PreparationRecoverySection from "./all-on-4/preparation-recovery-section";
import TestimonialsSection from "./all-on-4/testimonials-section";
import FAQSection from "./all-on-4/faq-section";
import FinalCTASection from "./shared/final-cta-section";
import StickyNavigation from "./shared/sticky-navigation";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";

gsap.registerPlugin(ScrollTrigger);

interface AllOnFourPageProps {
  treatment: Treatment;
}

export default function AllOnFourPage({ treatment }: AllOnFourPageProps) {
  const { t, locale } = useTranslation("all-on-4");
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize GSAP ScrollTrigger
    ScrollTrigger.refresh();

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const sections = [
    { id: "hero", label: t("navigation.sections.home") },
    { id: "what-is", label: t("navigation.sections.whatIs") },
    { id: "why-choose", label: t("navigation.sections.whyChoose") },
    { id: "timeline", label: t("navigation.sections.process") },
    { id: "types", label: t("navigation.sections.types") },
    { id: "benefits", label: t("navigation.sections.benefits") },
    { id: "gallery", label: t("navigation.sections.gallery") },
    { id: "cost", label: t("navigation.sections.cost") },
    { id: "candidates", label: t("navigation.sections.candidates") },
    { id: "preparation", label: t("navigation.sections.recovery") },
    { id: "testimonials", label: t("navigation.sections.stories") },
    { id: "faq", label: t("navigation.sections.faq") },
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-slate-50 dark:bg-navy-950 overflow-x-clip">
      <Header />

      {/* Treatment Header Section */}
      <section className="relative bg-gradient-to-b from-navy to-navy-950 border-b border-white/10 pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,rgba(210,161,56,0.08)_0%,transparent_60%)]" />
        <div className="container relative py-6">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-between mb-4">
            <Breadcrumbs />
            <Link
              href={`/${locale}/treatments/`}
              className="hidden md:flex items-center space-x-2 text-white/60 hover:text-gold transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t("navigation.backToTreatments")}</span>
            </Link>
          </div>
        </div>
      </section>

      <StickyNavigation sections={sections} />

      <main className="relative">
        <AllOnFourHeroSection treatment={treatment} />
        <WhatIsAllOnFourSection />
        <WhyChooseSection />
        <TreatmentTimelineSection />
        <TypesOfImplantsSection />
        <BenefitsSection />
        <GallerySection />
        <CostSection />
        <CandidateSection />
        <PreparationRecoverySection />
        <TestimonialsSection />
        <FAQSection />

        {/* Final CTA */}
        <FinalCTASection
          treatmentName={t("finalCta.treatmentName")}
          treatmentKeyword={t("finalCta.treatmentKeyword")}
          scrollToSection="gallery"
          customHeading={t("finalCta.heading")}
          customSubheading={t("finalCta.subheading")}
          customBenefits={ensureStringArray(t("finalCta.benefits", { returnObjects: true }))}
        />
      </main>

    </div>
  );
}
